import { Modal, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { paginatePrintPages, paginateDirectPrint } from '../../utils/paginatePrintPages';

// Utility functions for bonliv
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatNumber = (num) => (Number(num) || 0).toFixed(2);

// Calculate total TTC for an item (same as UI calculation)
const calculateTotalTTC = (qte, pvTTC, remise) => {
    const discountedPrice = (pvTTC * (1 - remise / 100));
    const total = qte * discountedPrice;
    // Round to 2 decimal places like Excel's ROUND function
    return Math.round(total * 100) / 100;
};

// Convert number to words in French
const numberToFrenchWords = (number) => {
    const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
    const scales = ['', 'mille', 'million', 'milliard', 'billion'];

    const convertChunk = (num) => {
        if (num === 0) return '';
        
        let words = '';
        const hundredsDigit = Math.floor(num / 100);
        let remainder = num % 100;
        
        // Handle hundreds
        if (hundredsDigit > 0) {
            words += hundredsDigit === 1 
                ? 'cent ' 
                : `${units[hundredsDigit]} cent${remainder === 0 ? 's ' : ' '}`;
        }
        
        // Handle tens and units
        if (remainder >= 20) {
            const tensIndex = Math.floor(remainder / 10);
            const unitsIndex = remainder % 10;
            
            if (tensIndex === 7 || tensIndex === 9) {
                words += tens[tensIndex];
                // Handle "et" for 71/91 specifically
                if (unitsIndex === 1) {
                    words += tensIndex === 7 ? ' et ' : '-';
                    words += units[10 + unitsIndex];
                } else {
                    words += unitsIndex > 0 ? `-${units[10 + unitsIndex]}` : '';
                }
                words += ' ';
            } else {
                words += tens[tensIndex];
                // Handle "et" for 21/31/41/etc.
                if (unitsIndex === 1 && tensIndex !== 8) {
                    words += ' et ';
                } else if (unitsIndex > 0) {
                    words += '-';
                }
                words += units[unitsIndex] + ' ';
            }

            // Add "s" to quatre-vingt ONLY when it's exactly 80 (no remainder)
            if (tensIndex === 8 && unitsIndex === 0) {
                words = words.replace(/quatre-vingt$/, 'quatre-vingts');
            }
        } else if (remainder > 0) {
            words += units[remainder] + ' ';
        }
        
        return words.trim() + ' ';
    };

    const convert = (num) => {
        if (num === 0) return 'zéro';
        
        let words = '';
        let scaleIndex = 0;
        
        while (num > 0) {
            const chunk = num % 1000;
            if (chunk > 0) {
                let chunkWords = convertChunk(chunk);
                let scaleWord = scales[scaleIndex];
                
                // Handle pluralization for millions/milliards
                if (scaleIndex >= 2 && chunk > 1) {
                    scaleWord += 's';
                }
                
                // Special case for "mille"
                if (scaleIndex === 1 && chunk === 1) {
                    chunkWords = '';
                }
                
                words = chunkWords + scaleWord + ' ' + words;
            }
            num = Math.floor(num / 1000);
            scaleIndex++;
        }
        
        return words.replace(/\s+/g, ' ').trim();
    };

    // Handle decimal part
    const wholePart = Math.floor(number);
    const decimalPart = Math.round((number - wholePart) * 100);
    
    let result = convert(wholePart);
    
    if (decimalPart > 0) {
        const centimes = decimalPart === 1 ? 'un centime' : `${convert(decimalPart)} centimes`;
        result += ` et ${centimes}`;
    }
    
    return result;
};

const bonlivTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Bon de Livraison - {{REF_BL}}</title>
    <style>
        @media print {
  .page { 
    page-break-after: always;
    padding-top: 30mm !important;  /* Force top padding */
    box-sizing: border-box;
  }
  .page:last-child { page-break-after: auto; }
  .content-table tr { break-inside: avoid; }
}
            @page { size: A4; margin: 1cm 1cm 1.1cm 1cm; }
        }
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 5px;
            font-size: 2px;
            line-height: 1;
        }
        .header-container {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            font-size: 11px;
        }
        .header-right {
            border: 1px solid black;
            padding: 5px;
            border-radius: 8px;
            overflow: hidden;
        }
        .header-left {
            width: 45%;
        }
        .header-right {
            width: 45%;
            text-align: left;
            padding: 10px;
        }


         .bl-title {
            display: flex;
            border: 1px solid black;
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 5px;
        }
        
        .bl-title div {
            padding: 8px 10px;
            font-weight: bold;
        }
        
        .bl-title div:first-child {
            flex: 1;
            border-right: 1px solid black;
        }
        
        .bl-title div:last-child {
            flex: 1;
            text-align: center;
        }


        .info-grid {
            display: grid;
            grid-template-columns: auto auto auto;
            border: 1px solid black;
            margin: 5px 0;
            border-radius: 8px;
            overflow: hidden;
        }
        .info-grid > div {
            border: 1px solid black;
            padding: 3px 5px;
        }
        .delivery-info {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr 1fr;
            border: 1px solid black;
            margin: 5px 0;
            border-radius: 8px;
            overflow: hidden;
            font-size: 11px;
        }
        .delivery-info > div {
            border: 1px solid black;
            padding: 3px 5px;
        }
        table.content-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            border-bottom: 1px solid black; /* Add bottom border to the table */
        }
        .content-table th {
            /* Combined rule removed */
            padding: 2px 3px;
            font-size: 11px;
            line-height: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            background: #fff;
            font-weight: normal;
            height: 18px; /* Slightly taller header */
            border: 1px solid black; /* Ensure full border for header */
        }
        .content-table td {
             /* Combined rule removed */
             border-style: none solid;
             border-width: 0 1px;
             border-color: black;
             padding: 2px 3px;
             font-size: 11px;
             line-height: 1;
             height: 16px; /* Fixed height for single line */
             white-space: nowrap;
             overflow: hidden;
             text-overflow: ellipsis;
        }
        /* Specific width and handling for designation column */
        .content-table td:nth-child(2) {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 300px; /* Adjust based on your needs */
        }
        .right { text-align: right; }
        .totals-section {
            margin-top: 10px;
            border-collapse: separate;
            border-spacing: 0;
            width: 100%;
            border-radius: 8px;
            overflow: hidden;
            font-size: 11px;
        }
        .totals-section td {
            border: 1px solid black;
            padding: 3px;
        }
        .amount-in-words {
            margin-top: 10px;
            font-style: italic;
            font-size: 10px;
        }
        .footer-note {
            margin-top: 5px;
            font-size: 9px;
            font-style: italic;
        }
    </style>
</head>
<body>
    {{PAGES}}
</body>
</html>`;

const pageTemplate = `
<div class="page">
    <div class="header-container">
        <div class="header-left">
            <div class="bl-title">
            <div>Bon de livraison N° :</div>
            <div>{{REF_BL}}</div>
        </div>
        <div class="info-grid">
            <div>Date</div>
            <div>Code client</div>
            <div>N° page</div>
            <div>{{DATE_BL}}</div>
            <div>{{CODE_CLIENT}}</div>
            <div data-page-number>{{CURRENT_PAGE}} / {{TOTAL_PAGES}}</div>
        </div>
            
        </div>
        <div class="header-right">
            {{CLIENT}}<br>
            {{CLIENT_ADDRESS}}
        </div>
    </div>
    
    <div class="delivery-info">
        <div>N° Cde</div>
        <div>Représentant</div>
        <div>M_Livraison</div>
        <div>M_paiement</div>
        <div>{{NUM_COMMANDE}}</div>
        <div>{{COMMERCIAL}}</div>
        <div>{{MODELIV}}</div>
        <div>{{MODE_PAIE}}</div>
    </div>

    <table class="content-table">
        <thead>
            <tr>
                <th>Référence</th>
                <th>Désignation</th>
                <th class="right">Qté</th>
                <th class="right">PU ttc</th>
                <th class="right">R%</th>
                <th class="right">Mnt ttc</th>
            </tr>
        </thead>
        <tbody>
            {{ITEMS_ROWS}}
        </tbody>
    </table>
    {{LAST_PAGE_CONTENT}}
</div>`;

const generatePages = (bonliv, items) => {
    // Sort items by article code (code_art)
    const sortedItems = [...items].sort((a, b) => 
      (a.CODE_ART || '').localeCompare(b.CODE_ART || '')
    );

    // Pagination is measured after the output's page styles are applied.
    let rowsHtml = '';

    sortedItems.forEach(item => {
        const calculatedTotal = calculateTotalTTC(item.QTE, item.PV_TTC, item.REMISE);
        rowsHtml += `
            <tr>
                <td>${item.article?.REF_OEM || ''}</td>
                <td>${item.ARTICLE || ''}</td>
                <td class="right">${item.QTE}</td>
                <td class="right">${formatNumber(item.PV_TTC)}</td>
                <td class="right">${formatNumber(item.REMISE)}</td>
                <td class="right">${formatNumber(calculatedTotal)}</td>
            </tr>`;
    });

    const lastPageContent = `
            <table class="totals-section">
                <tr>
                     <td style="width: 50%">Total HT</td>
                    <td style="width: 35%">Tva 20%</td>
                    <td style="width: 15%">Total ttc</td>
                </tr>
                <tr>
                    <td>${formatNumber(bonliv.MNT_HT)}</td>
                    <td>${formatNumber(bonliv.MNT_TTC - bonliv.MNT_HT)}</td>
                    <td>${formatNumber(bonliv.MNT_TTC)}</td>
                </tr>
            </table>
            <div class="amount-in-words">
                Le présent Bon Livraison est arrêté à la somme de : ${numberToFrenchWords(Math.floor(bonliv.MNT_TTC))} DH ${Math.round((bonliv.MNT_TTC % 1) * 100)} centimes
            </div>
            <div class="footer-note">
                Les marchandises voyagent aux risques & périls du destinataire. dépassé 15 jours la marchandise n'est ni reprise ni échangée.
            </div>`;

    return processTemplate(pageTemplate, {
        REF_BL: bonliv.REF_FAC ? bonliv.REF_FAC.slice(2) : '',
        DATE_BL: formatDate(bonliv.DATE_FAC),
        CODE_CLIENT: bonliv.COMPTE,
        CLIENT: bonliv.CLIENT,
        CLIENT_ADDRESS: bonliv.client?.VILLE || '', // Safe navigation
        NUM_COMMANDE: bonliv.NUM_COMMANDE || '',
        COMMERCIAL: '', // Consider passing commercial if available
        MODE_PAIE: bonliv.MODE_PAIE,
        MODELIV: bonliv.MODELIV,
        CURRENT_PAGE: 1,
        TOTAL_PAGES: 1,
        ITEMS_ROWS: rowsHtml,
        LAST_PAGE_CONTENT: lastPageContent
    });
};

const processTemplate = (template, replacements) => {
    let processedTemplate = template;
    Object.entries(replacements).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedTemplate = processedTemplate.replace(regex, value || '');
    });
    return processedTemplate;
};

// PDF generation helper function
const generatePdfBlob = async (htmlString) => {
    const container = document.createElement('div');
    container.style.width = '210mm';
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.innerHTML = htmlString;
    document.body.appendChild(container);
    
    try {
      const sourcePage = container.querySelector('.page');
      const margin = 10; // 10mm for sides/bottom
      sourcePage.style.width = '210mm';
      sourcePage.style.height = '297mm';
      sourcePage.style.padding = `30mm ${margin}mm ${margin}mm 15mm ${margin}mm`;
      sourcePage.style.paddingBottom = '6mm';
      sourcePage.style.boxSizing = 'border-box';
      sourcePage.style.backgroundColor = '#FFFFFF';
      const pages = await paginatePrintPages(container, { pageHeightMm: 297 });
      const pdf = new jsPDF('p', 'mm', 'a4');
  
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        
        const canvas = await html2canvas(page, {
            scale: 3,
            useCORS: true,
            logging: false,
            letterRendering: true,
            backgroundColor: '#FFFFFF',
            width: page.offsetWidth,
            height: page.offsetHeight,
            fontFamily: 'Arial',
        });
        // Add image to PDF
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297); // Direct A4 dimensions
      }
  
      return pdf.output('blob');
    } finally {
      document.body.removeChild(container);
    }
};

// Main export function
// Main export function
export const printBonliv = async (bonliv, items) => {
    try {
      // Create loading modal with "Sortir" button
      const loadingModalDiv = document.createElement('div');
      document.body.appendChild(loadingModalDiv);
      
      // Import Button if not already imported
      // import { Modal, Spin, Button } from 'antd';
      
      const closeLoadingModal = () => {
        ReactDOM.unmountComponentAtNode(loadingModalDiv);
        document.body.removeChild(loadingModalDiv);
      };
      
      ReactDOM.render(
        <Modal
          title="Generating PDF"
          open={true}
          closable={true}
          maskClosable={false}
          footer={[
            <Button key="exit" onClick={closeLoadingModal}>
              Sortir
            </Button>
          ]}
        >
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              tip="Preparing document..."
            />
          </div>
        </Modal>,
        loadingModalDiv
      );
  
      // Generate pages content and full HTML
      const pagesContent = generatePages(bonliv, items);
      const html = processTemplate(bonlivTemplate, {
        REF_BL: bonliv.REF_BL,
        PAGES: pagesContent,
      });
  
      // Generate the PDF blob
      const pdfBlob = await generatePdfBlob(html);
      
      // Close loading modal
      closeLoadingModal();
  
      // Create URL for the blob
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Create preview modal with "Sortir" button
      const previewModalDiv = document.createElement('div');
      document.body.appendChild(previewModalDiv);
      
      const closePreviewModal = () => {
        URL.revokeObjectURL(pdfUrl);
        ReactDOM.unmountComponentAtNode(previewModalDiv);
        document.body.removeChild(previewModalDiv);
      };
      
      ReactDOM.render(
        <Modal
          title={`PDF Preview - ${bonliv.REF_BL}`}
          open={true}
          width="90%"
          centered={true}
          closable={true}
          maskClosable={true}
          className="pdf-preview-modal"
          style={{ top: 20, maxWidth: '1400px' }}
          footer={[
            <Button key="exit" onClick={closePreviewModal}>
              Sortir
            </Button>
          ]}
          onCancel={closePreviewModal}
        >
          <div className="pdf-preview-container">
            <iframe
              src={pdfUrl}
              style={{ width: '100%', height: '80vh', border: 'none' }}
              title="PDF Preview"
            />
          </div>
        </Modal>,
        previewModalDiv
      );
  
      return {
        cleanup: closePreviewModal,
        pdfUrl
      };
  
    } catch (error) {
      console.error('Error generating bonliv:', error);
      Modal.error({
        title: 'Error',
        content: `Failed to generate document: ${error.message}`,
      });
      throw new Error(`Failed to generate document: ${error.message}`);
    }
  };


// Similar functions for other document types
export const printBonlivDirect = async (bonliv, items, commercial = null) => {
  try {
      // Generate pages content and full HTML (using bonliv template)
      const pagesContent = generatePages(bonliv, items);
      const html = processTemplate(bonlivTemplate, {
          REF_BL: bonliv.REF_BL,
          PAGES: pagesContent,
      });

      // Create an invisible iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position: absolute; width: 190mm; height: 297mm; left: -9999px; border: 0;';
      document.body.appendChild(iframe);
      
      // Write the HTML content to the iframe
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      // Set up cleanup after printing
      iframe.onload = () => {
          setTimeout(async () => {
              try {
                  await paginateDirectPrint(doc, 276); // A4 minus 10 mm top and 11 mm bottom margins.
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                  
                  // Remove the iframe after printing
                  setTimeout(() => {
                      document.body.removeChild(iframe);
                  }, 1000);
              } catch (err) {
                  console.error('Error in print process:', err);
                  document.body.removeChild(iframe);
              }
          }, 500);
      };
      doc.open();
      doc.write(html);
      doc.close();
  } catch (error) {
      console.error('Error generating bon de livraison for direct print:', error);
      throw new Error(`Failed to generate document: ${error.message}`);
  }
};
